import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type PropsWithChildren,
} from 'react'
import {
  type FeedbackPayload,
  type FeedbackState,
  type FeedbackType,
  type ErrorInfo,
  type ChatContext,
} from '@/types/feedback'
import { apiFetch } from '../../lib/apiClient'
import { useSession } from '@/features/auth/hooks/useSession'
import { registerFeedbackOpener } from './feedbackBridge'

type Action =
  | { type: 'open'; payload?: { feedbackType?: FeedbackType; errorInfo?: ErrorInfo; chatContext?: ChatContext } }
  | { type: 'close' }
  | { type: 'set_type'; payload: FeedbackType }
  | { type: 'set_description'; payload: string }
  | { type: 'set_sending'; payload: boolean }
  | { type: 'set_error'; payload: string | null }
  | { type: 'set_success'; payload: boolean }
  | { type: 'add_screenshots'; payload: Array<{ file: File; preview: string }> }
  | { type: 'remove_screenshot'; payload: number }
  | { type: 'clear_screenshots' }
  | { type: 'clear_context' }
  | {
      type: 'set_conversation_context';
      payload: {
        workspaceId: string | null;
        conversationId: string | null;
        conversationName: string | null;
      };
    }

const initialState: FeedbackState = {
  open: false,
  type: 'bug_report',
  description: '',
  sending: false,
  error: null,
  success: false,
  errorInfo: null,
  chatContext: null,
  screenshots: [],
  workspaceId: null,
  conversationId: null,
  conversationName: null,
}

function reducer(state: FeedbackState, action: Action): FeedbackState {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        open: true,
        success: false,
        error: null,
        type: action.payload?.feedbackType ?? 'bug_report',
        errorInfo: action.payload?.errorInfo ?? null,
        chatContext: action.payload?.chatContext ?? null,
      }
    case 'close':
      // Revoke object URLs to prevent memory leaks
      state.screenshots.forEach(s => URL.revokeObjectURL(s.preview))
      return {
        ...state,
        open: false,
        description: '',
        sending: false,
        error: null,
        errorInfo: null,
        chatContext: null,
        screenshots: [],
      }
    case 'set_type':
      return { ...state, type: action.payload, success: false }
    case 'set_description':
      return { ...state, description: action.payload, success: false }
    case 'set_sending':
      return { ...state, sending: action.payload }
    case 'set_error':
      return { ...state, error: action.payload }
    case 'set_success':
      return { ...state, success: action.payload }
    case 'add_screenshots':
      return { ...state, screenshots: [...state.screenshots, ...action.payload] }
    case 'remove_screenshot':
      if (state.screenshots[action.payload]) {
        URL.revokeObjectURL(state.screenshots[action.payload].preview)
      }
      return { ...state, screenshots: state.screenshots.filter((_, i) => i !== action.payload) }
    case 'clear_screenshots':
      state.screenshots.forEach(s => URL.revokeObjectURL(s.preview))
      return { ...state, screenshots: [] }
    case 'clear_context':
      return { ...state, errorInfo: null, chatContext: null }
    case 'set_conversation_context':
      return {
        ...state,
        workspaceId: action.payload.workspaceId,
        conversationId: action.payload.conversationId,
        conversationName: action.payload.conversationName,
      }
    default:
      return state
  }
}

interface FeedbackContextValue extends FeedbackState {
  openModal(options?: { type?: FeedbackType; errorInfo?: ErrorInfo; chatContext?: ChatContext }): void
  openWithContext(type: FeedbackType, options?: { errorInfo?: ErrorInfo; chatContext?: ChatContext }): void
  closeModal(): void
  setType(type: FeedbackType): void
  setDescription(value: string): void
  addScreenshots(files: FileList | File[]): void
  removeScreenshot(index: number): void
  setConversationContext(value: {
    workspaceId?: string | null
    conversationId?: string | null
    conversationName?: string | null
  } | null): void
  submit(): Promise<void>
  submitQuickFeedback(type: 'thumbs_up' | 'thumbs_down', chatContext: ChatContext): Promise<void>
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined)

export function FeedbackProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { token } = useSession()

  const openModal = useCallback((options?: { type?: FeedbackType; errorInfo?: ErrorInfo; chatContext?: ChatContext }) => {
    dispatch({
      type: 'open',
      payload: {
        feedbackType: options?.type,
        errorInfo: options?.errorInfo,
        chatContext: options?.chatContext,
      },
    })
  }, [])

  const closeModal = useCallback(() => {
    dispatch({ type: 'close' })
  }, [])

  const openWithContext = useCallback(
    (type: FeedbackType, options?: { errorInfo?: ErrorInfo; chatContext?: ChatContext }) => {
      openModal({ type, errorInfo: options?.errorInfo, chatContext: options?.chatContext })
    },
    [openModal],
  )

  const setType = useCallback((feedbackType: FeedbackType) => {
    dispatch({ type: 'set_type', payload: feedbackType })
  }, [])

  const setDescription = useCallback((value: string) => {
    dispatch({ type: 'set_description', payload: value })
  }, [])

  const addScreenshots = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files)
    const imageFiles = fileArray.filter(f => f.type.startsWith('image/'))
    if (imageFiles.length === 0) return

    const newScreenshots = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }))
    dispatch({ type: 'add_screenshots', payload: newScreenshots })
  }, [])

  const removeScreenshot = useCallback((index: number) => {
    dispatch({ type: 'remove_screenshot', payload: index })
  }, [])

  const setConversationContext = useCallback((value: {
    workspaceId?: string | null
    conversationId?: string | null
    conversationName?: string | null
  } | null) => {
    dispatch({
      type: 'set_conversation_context',
      payload: {
        workspaceId: value?.workspaceId ?? null,
        conversationId: value?.conversationId ?? null,
        conversationName: value?.conversationName ?? null,
      },
    })
  }, [])

  const submit = useCallback(async () => {
    const trimmed = state.description.trim()

    const needsDescription = (() => {
      if (state.type === 'thumbs_up' || state.type === 'thumbs_down') {
        return false
      }
      if (state.type === 'error_report') {
        return !state.errorInfo
      }
      if (state.type === 'chat_response') {
        return !state.chatContext
      }
      return true
    })()

    if (needsDescription && !trimmed) {
      dispatch({ type: 'set_error', payload: 'Please provide a description.' })
      return
    }

    if (!token) {
      dispatch({
        type: 'set_error',
        payload: 'Please log in to submit feedback.',
      })
      return
    }

    dispatch({ type: 'set_sending', payload: true })
    dispatch({ type: 'set_error', payload: null })

    const payload: FeedbackPayload = {
      type: state.type,
      description: trimmed || undefined,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      workspaceId: state.workspaceId ?? undefined,
      conversationId: state.conversationId ?? undefined,
      conversationName: state.conversationName ?? undefined,
    }

    if (state.errorInfo) {
      payload.errorInfo = state.errorInfo
    }

    if (state.chatContext) {
      payload.chatContext = state.chatContext
    }

    let requestHeaders: HeadersInit | undefined
    let requestBody: BodyInit

    if (state.screenshots.length > 0) {
      const formData = new FormData()
      formData.append('payload', JSON.stringify(payload))
      state.screenshots.forEach((s, i) => {
        formData.append('screenshots', s.file, `screenshot-${i}.${s.file.name.split('.').pop()}`)
      })
      requestBody = formData
    } else {
      requestBody = JSON.stringify(payload)
      requestHeaders = { 'Content-Type': 'application/json' }
    }

    try {
      await apiFetch('feedback', {
        method: 'POST',
        authToken: token,
        ...(requestHeaders ? { headers: requestHeaders } : {}),
        body: requestBody,
      })

      dispatch({ type: 'set_success', payload: true })
      dispatch({ type: 'set_description', payload: '' })

      // Auto-close after success
      setTimeout(() => {
        dispatch({ type: 'close' })
      }, 1500)
    } catch (error) {
      console.error('Feedback submission failed', error)
      dispatch({
        type: 'set_error',
        payload: 'Failed to send feedback. Please try again.',
      })
    } finally {
      dispatch({ type: 'set_sending', payload: false })
    }
  }, [
    state.description,
    state.type,
    state.errorInfo,
    state.chatContext,
    state.screenshots,
    state.workspaceId,
    state.conversationId,
    state.conversationName,
    token,
  ])

  // Quick feedback for thumbs up/down (no modal needed)
  const submitQuickFeedback = useCallback(async (type: 'thumbs_up' | 'thumbs_down', chatContext: ChatContext) => {
    if (!token) {
      console.warn('Cannot submit feedback without authentication')
      return
    }

    const payload: FeedbackPayload = {
      type,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      chatContext,
      workspaceId: state.workspaceId ?? undefined,
      conversationId: state.conversationId ?? undefined,
      conversationName: state.conversationName ?? undefined,
    }

    try {
      await apiFetch('feedback', {
        method: 'POST',
        authToken: token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      console.log(`[Feedback] ${type} submitted for message ${chatContext.messageId}`)
    } catch (error) {
      console.error('Quick feedback submission failed', error)
    }
  }, [
    state.workspaceId,
    state.conversationId,
    state.conversationName,
    token,
  ])

  // Expose the modal opener to ToastItem, which is mounted above this
  // provider (ToastProvider sits outside the auth gate) and so cannot use
  // useFeedback() — see feedbackBridge.ts.
  useEffect(
    () => registerFeedbackOpener((errorInfo) => openWithContext('bug_report', { errorInfo })),
    [openWithContext],
  )

  const value = useMemo<FeedbackContextValue>(
    () => ({
      ...state,
      openModal,
      openWithContext,
      closeModal,
      setType,
      setDescription,
      addScreenshots,
      removeScreenshot,
      setConversationContext,
      submit,
      submitQuickFeedback,
    }),
    [
      state,
      openModal,
      openWithContext,
      closeModal,
      setType,
      setDescription,
      addScreenshots,
      removeScreenshot,
      setConversationContext,
      submit,
      submitQuickFeedback,
    ],
  )

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  )
}

export function useFeedback() {
  const context = useContext(FeedbackContext)
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider')
  }
  return context
}
